import React, {
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from "react";
import { Checkbox, CheckboxGroup, FieldError } from "react-aria-components";
import { t } from "ttag";

import { constants, reverse } from "@reactivated";

import { CSRF_HEADER, loadCSRFToken } from "@thelabnyc/thelabui/src/utils/api";
import { richT } from "@thelabnyc/thelabui/src/utils/richText";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { loadFormToken, sleep } from "../../api/forms";
import { ReactivatedContext as Context } from "../../context";
import { ButtonPrimary } from "../Clickables";
import { LogoLoader } from "../LogoLoader";
import { Svg } from "../Svg";

import formStyles from "../Forms/index.module.scss";
import styles from "./forms.module.scss";

export const FormType = constants["server.cms.blocks.forms.Forms"];
export type FormType = (typeof FormType)[keyof typeof FormType];

export interface FormLayout {
    /**
     * Naming is hard.
     * Solo = the form exists basically entirely on its own
     * Complementary = the form exists in context with some other stuff
     * Full Width = other ones have pixel max-widths; this doesn't
     */
    layout?: "solo" | "complementary" | "full-width" | "three-column";
    custom_submit_url: string | null;
}

interface CRMForm extends FormLayout {
    type: FormType;
    className?: string;
    title?: string;
    successState?: React.JSX.Element;
    tweakFormDataBeforeSubmit?: (data: FormData) => Promise<FormData>;
    onSubmit?: (resp: Response) => void;
    children: (
        disabled: boolean,
        legal: React.JSX.Element,
    ) => React.JSX.Element;
}

export const Legal = ({
    href,
    className,
    formType,
}: {
    href: string;
    className?: string;
    formType?: FormType;
}) => {
    const context = useContext(Context);

    const ohCanada = context.LOCALE === "en-ca" || context.LOCALE === "fr-ca";

    const linkTag = (children?: readonly React.ReactNode[]) => (
        <a
            href={href}
            className={styles.textLink}
            onClick={(event) => {
                event.stopPropagation();
            }}
        >
            {children}
        </a>
    );

    const privacyText = (
        <p className={concatClassNames([styles.legal, className])}>
            {ohCanada
                ? richT(
                      t`I agree with the <link>Privacy Policy</link> and provide my consent to receive marketing emails from LG Canada.`,
                      { link: linkTag },
                  )
                : richT(
                      t`By clicking the button below, you agree to the <link>Privacy Policy</link> and to receiving emails from SKS with news, special offers, and future messages that may interest you.`,
                      { link: linkTag },
                  )}
        </p>
    );

    const checkbox = (
        <CheckboxGroup isRequired>
            <Checkbox name="Opt-In" value="True" className={styles.checkbox}>
                <Svg
                    name="check"
                    className={styles.checkSvg}
                    aria-hidden="true"
                />
                {privacyText}
            </Checkbox>
            <FieldError className={formStyles.errorMessage}>
                {({ validationDetails, validationErrors }) =>
                    validationDetails.valueMissing
                        ? t`This field is required.`
                        : validationErrors
                }
            </FieldError>
        </CheckboxGroup>
    );

    /**
     * We want a checkbox for Canadian forms, but only if it's not "Stay Informed",
     * since the form's raison d'être is to get consent to be emailed
     */
    const showOptInCheckbox = ohCanada && formType !== FormType.STAY_INFORMED;

    return (
        <div className={styles.legalWrapper}>
            {showOptInCheckbox ? checkbox : privacyText}
        </div>
    );
};

const DefaultSuccessState = () => (
    <>
        <h2 className={styles.title}>{t`Thank you for contacting us!`}</h2>
        <p>
            {t`Your message was sent and we will be in touch with you shortly.`}
        </p>
    </>
);

export const CRMForm = (props: CRMForm) => {
    const formRef = useRef<HTMLFormElement | null>(null);
    const loadingRef = useRef<HTMLDivElement | null>(null);
    const successRef = useRef<HTMLDivElement | null>(null);
    const failureRef = useRef<HTMLDivElement | null>(null);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [failure, setFailure] = useState(false);
    const [formToken, setFormToken] = useState<{
        value: string;
        readyAt: number;
        fromRetry: boolean;
    } | null>(null);
    const context = useContext(Context);

    const acquireFormToken = useCallback((fromRetry: boolean) => {
        loadFormToken()
            .then((token) => {
                setFormToken({ value: token, readyAt: Date.now(), fromRetry });
            })
            .catch((error: unknown) => {
                console.warn("Failed to load form token:", error);
            });
    }, []);

    useEffect(() => {
        const formElement = formRef.current;
        if (formElement === null) {
            return;
        }
        // Fetch the token as the form approaches the viewport, instead of on
        // mount: the footer newsletter form is on nearly every page, and an
        // eager fetch would hit the origin on every CDN-served page view.
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    acquireFormToken(false);
                    observer.disconnect();
                }
            },
            { rootMargin: "200px" },
        );
        observer.observe(formElement);
        return () => {
            observer.disconnect();
        };
    }, [acquireFormToken]);

    useEffect(() => {
        if (loading === true && loadingRef.current) {
            loadingRef.current.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
        }
    }, [loading]);

    useEffect(() => {
        if (success === true && successRef.current) {
            successRef.current.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
        }
    }, [success]);

    useEffect(() => {
        if (failure === true && failureRef.current) {
            failureRef.current.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
        }
    }, [failure]);

    const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (formToken === null) {
            // The server discards tokenless submissions as spam, so showing
            // the failure state beats a false thank-you. Retry the token
            // fetch so the user's next attempt can succeed.
            setFailure(true);
            acquireFormToken(true);
            return;
        }

        setLoading(true);

        const action = event.currentTarget.action;
        const method = event.currentTarget.method;
        let data = new FormData(event.currentTarget);

        // A token fetched by the failure-path retry can be younger than the
        // server's minimum fill time; wait out the difference so the retry
        // isn't discarded as spam. First-attempt tokens are never held.
        if (formToken.fromRetry) {
            const minTokenAgeMs = constants.MIN_FILL_SECONDS * 1000;
            const tokenAgeMs = Date.now() - formToken.readyAt;
            if (tokenAgeMs < minTokenAgeMs) {
                await sleep(minTokenAgeMs - tokenAgeMs);
            }
        }
        if (props.tweakFormDataBeforeSubmit) {
            data = await props.tweakFormDataBeforeSubmit(data);
        }
        const resp = await fetch(action, {
            method: method,
            headers: {
                [CSRF_HEADER]: await loadCSRFToken(),
            },
            body: data,
        });

        if (resp.ok) {
            setLoading(false);
            setSuccess(true);
        } else {
            setLoading(false);
            setFailure(true);
        }

        if (props.onSubmit) {
            props.onSubmit(resp);
        }
    };

    const pageTitle = props.title
        ? props.title
        : typeof document !== "undefined"
          ? document.title
          : "";

    return (
        <form
            ref={formRef}
            className={concatClassNames([
                props.layout === "solo" ? styles.soloForm : undefined,
                props.layout === "complementary"
                    ? styles.complementaryForm
                    : undefined,
                props.layout === "full-width"
                    ? styles.fullWidthForm
                    : undefined,
                props.layout === "three-column"
                    ? styles.threeColumnForm
                    : undefined,
                loading ? styles.loading : undefined,
                props.className,
            ])}
            method="post"
            onSubmit={onSubmit}
            action={reverse("cms-api:submit-form", {
                form_type: props.type,
            })}
            acceptCharset="utf-8"
        >
            {loading && (
                <div ref={loadingRef} className={styles.loadingUI}>
                    {t`Sending`}
                    <LogoLoader className={styles.loader} />
                </div>
            )}
            {success && (
                <div ref={successRef} className={styles.successUI}>
                    {props.successState ?? <DefaultSuccessState />}
                </div>
            )}
            {failure && (
                <div ref={failureRef} className={styles.failureUI}>
                    <h2 className={styles.title}>{t`Something went wrong.`}</h2>
                    <p>{t`Please try again later.`}</p>
                    <ButtonPrimary onPress={() => setFailure(false)}>
                        {t`Return to Form`}
                    </ButtonPrimary>
                </div>
            )}
            <input type="hidden" name="locale" value={context.LOCALE} />
            <input type="hidden" name="page_title" value={pageTitle} />
            <input type="hidden" name="product_name" value={pageTitle} />
            <input type="hidden" name="Product_Name_ _c" value={pageTitle} />
            <input type="hidden" name="referer" value={context.request.url} />
            {formToken !== null && (
                <input
                    type="hidden"
                    name={constants.TIMING_FIELD_NAME}
                    value={formToken.value}
                />
            )}
            {/* Honeypot! */}
            <div className={styles.extraField} aria-hidden="true">
                <label>
                    {t`Leave this field blank`}
                    <input
                        type="text"
                        name={constants.HONEYPOT_FIELD_NAME}
                        tabIndex={-1}
                        autoComplete="nope"
                    />
                </label>
            </div>
            {props.custom_submit_url && (
                <input
                    type="hidden"
                    name="custom_submit_url"
                    value={props.custom_submit_url}
                />
            )}
            {props.children(
                loading === true,
                <Legal
                    formType={props.type}
                    href={context.wagtail_links.privacy_policy}
                />,
            )}
        </form>
    );
};
